/**
 * GPS定位服务
 * 1.3.0: 提供GPS定位相关工具函数
 * - 定位权限检查
 * - 位置获取
 * - 错误处理
 */

import { logger } from '../utils/logger';

export interface GeolocationPosition {
  lat: number;
  lng: number;
}

export interface GeolocationError {
  code: number;
  message: string;
}

/**
 * 检查浏览器是否支持Geolocation API
 */
export function isGeolocationSupported(): boolean {
  return 'geolocation' in navigator;
}

/**
 * 请求定位权限并获取当前位置
 * @param options 定位选项
 * @returns Promise<GeolocationPosition> 位置信息
 */
export async function getCurrentPosition(
  options?: PositionOptions
): Promise<GeolocationPosition> {
  if (!isGeolocationSupported()) {
    throw new Error('浏览器不支持定位功能');
  }

  // 1.3.0: 确保使用最新的位置，不使用缓存，强制触发权限请求
  const defaultOptions: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 15000, // 15秒超时，给用户更多时间响应权限请求
    maximumAge: 0, // 不使用缓存，强制获取最新位置，确保触发权限请求
    ...options,
  };

  logger.log('📍 开始请求GPS定位权限...', {
    enableHighAccuracy: defaultOptions.enableHighAccuracy,
    timeout: defaultOptions.timeout,
    maximumAge: defaultOptions.maximumAge,
  });

  return new Promise((resolve, reject) => {
    // 1.3.0: 使用 getCurrentPosition 触发浏览器的权限请求弹窗
    // 这会确保浏览器显示定位权限弹窗，并在设置中显示定位权限开关
    // 注意：在HTTP环境下，某些浏览器可能不会显示权限弹窗，但会记录权限状态
    
    let hasResolved = false;
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (hasResolved) return; // 防止重复调用
        hasResolved = true;
        
        const location: GeolocationPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        
        logger.log('✅ GPS定位成功:', {
          lat: location.lat,
          lng: location.lng,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        });
        
        resolve(location);
      },
      (error) => {
        if (hasResolved) return; // 防止重复调用
        hasResolved = true;
        
        const errorMessage = handleLocationError(error);
        logger.error('❌ GPS定位失败:', {
          errorMessage,
          code: error.code,
          message: error.message,
          codeNames: {
            [error.PERMISSION_DENIED]: 'PERMISSION_DENIED',
            [error.POSITION_UNAVAILABLE]: 'POSITION_UNAVAILABLE',
            [error.TIMEOUT]: 'TIMEOUT',
          },
        });
        
        // 如果是权限被拒绝，说明浏览器已经记录了权限状态
        // 即使没有显示弹窗，权限状态也会在浏览器设置中显示
        if (error.code === error.PERMISSION_DENIED) {
          logger.warn('⚠️ 定位权限被拒绝。请检查浏览器设置中的定位权限。');
        }
        
        reject(new Error(errorMessage));
      },
      defaultOptions
    );
  });
}

/**
 * 请求定位权限（不获取位置）
 * 通过尝试获取位置来触发权限请求
 * @returns Promise<boolean> 是否获得权限
 */
export async function requestLocationPermission(): Promise<boolean> {
  if (!isGeolocationSupported()) {
    return false;
  }

  try {
    // 尝试获取位置来触发权限请求
    await getCurrentPosition({ timeout: 5000 });
    return true;
  } catch (error) {
    // 如果是权限被拒绝，返回false
    if (error instanceof Error) {
      if (error.message.includes('权限') || error.message.includes('denied')) {
        return false;
      }
    }
    // 其他错误（如超时）也返回false，但可能是临时问题
    return false;
  }
}

/**
 * 处理定位错误
 * @param error GeolocationPositionError
 * @returns 友好的错误消息
 */
export function handleLocationError(error: GeolocationPositionError): string {
  let message = '定位失败';
  
  switch (error.code) {
    case error.PERMISSION_DENIED:
      message = '定位权限被拒绝，请在浏览器设置中允许定位权限';
      break;
    case error.POSITION_UNAVAILABLE:
      message = '无法获取位置信息，请检查GPS是否开启';
      break;
    case error.TIMEOUT:
      message = '定位请求超时，请重试';
      break;
    default:
      message = `定位失败: ${error.message || '未知错误'}`;
      break;
  }
  
  return message;
}

/**
 * 检查定位权限状态
 * @returns Promise<'granted' | 'denied' | 'prompt' | 'unsupported'>
 */
export async function checkLocationPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unsupported'> {
  if (!isGeolocationSupported()) {
    return 'unsupported';
  }

  // 使用 Permissions API 检查权限（如果支持）
  if ('permissions' in navigator && 'query' in navigator.permissions) {
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      return result.state as 'granted' | 'denied' | 'prompt';
    } catch (e) {
      // Permissions API 可能不支持或失败，回退到尝试获取位置
      logger.warn('Permissions API 不可用，回退到位置获取方式');
    }
  }

  // 回退方案：尝试获取位置来判断权限
  // 注意：这可能会触发权限请求，但超时时间很短（1秒），主要用于判断权限状态
  try {
    await getCurrentPosition({ timeout: 1000, maximumAge: 0 });
    return 'granted';
  } catch (error) {
    if (error instanceof Error) {
      // 检查错误代码，PERMISSION_DENIED (1) 表示权限被拒绝
      if (error.message.includes('权限') || 
          error.message.includes('denied') ||
          error.message.includes('PERMISSION_DENIED')) {
        return 'denied';
      }
    }
    // 其他错误（超时、位置不可用等）可能表示权限状态为 'prompt'
    // 或者权限已授予但位置获取失败
    return 'prompt';
  }
}

